import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import type { GameSessionView, SpreadSessionView } from '@creativitas/shared';

export type RealtimeStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export function useSessionRealtime<TSnapshot extends SpreadSessionView | GameSessionView>({
  sessionId,
  guestToken,
  enabled = true,
  onSnapshot,
  onRemoved,
}: {
  sessionId: string;
  guestToken?: string;
  enabled?: boolean;
  onSnapshot: (snapshot: TSnapshot) => void;
  /** Ведущий удалил этого участника: сервер закрыл соединение и больше его не пустит. */
  onRemoved?: () => void;
}): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>(enabled ? 'connecting' : 'disconnected');
  const snapshotHandler = useRef(onSnapshot);
  snapshotHandler.current = onSnapshot;
  const removedHandler = useRef(onRemoved);
  removedHandler.current = onRemoved;

  useEffect(() => {
    if (!enabled || !sessionId) {
      setStatus('disconnected');
      return;
    }

    const socket = io({
      path: '/socket.io',
      /* Только опрос внутри HTTP/2-соединения сайта — см. `transports` на сервере. */
      transports: ['polling'],
      withCredentials: true,
      reconnection: true,
      autoConnect: false,
    });

    const join = () => {
      setStatus('connecting');
      socket.emit(
        'session:join',
        { sessionId, ...(guestToken ? { guestToken } : {}) },
        (result: { ok: boolean; message?: string }) => setStatus(result.ok ? 'connected' : 'error'),
      );
    };
    socket.on('connect', join);
    socket.on('disconnect', () => setStatus('disconnected'));
    socket.on('connect_error', () => setStatus('error'));
    socket.on('session:snapshot', (snapshot: TSnapshot) => snapshotHandler.current(snapshot));
    socket.on('session:removed', () => removedHandler.current?.());
    socket.connect();

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [enabled, guestToken, sessionId]);

  return status;
}
